/* eslint-disable line-comment-position */
// @flow
import React, { Fragment } from 'react';
import { Head } from '@finect/tabular-components/Head';
import { H1 } from '@finect/tabular-core/Headings';
import { withParams } from '@finect/front-core/withParams';
import type { WithParamsProps } from '@finect/front-core/withParams';
import { Datalayer } from '@finect/front-gtm';
import { buildOpenGraph, buildTwitter } from '../../data/seo';
import { ADVISERS_PER_PAGE as PER_PAGE, fetchAdvisers } from '../../data/advisers';
import {
  AdvisersPager,
  BottomCTA,
  BottomNavigation,
  CardList,
  InsideNavigation,
  ListHeader,
  MainGrey,
  NewSectionInner
} from '../../components';
import type { ProfessionalType } from '../../types';
import { ProfessionalCard } from '../Province/components/ProfessionalCard';

type Props = {
  staticContext?: Object,
  history: Object,
  match: Object,

  toggleModal: Function
} & WithParamsProps;

type State = {
  fetching: boolean,
  professionals: Array<ProfessionalType>,
  paging: Object,
  page: number,
  offset: number | null
};

const getPagePath = (page?: number) =>
  `/asesores/encuentra-un-asesor/profesionales${page && page > 1 ? `?page=${page}` : ''}`;

class Professionals extends React.Component<Props, State> {
  static loadInitialData(store, cookies, match, params) {
    const offset = params.page > 0 ? (params.page - 1) * PER_PAGE : null;

    return fetchAdvisers({
      offset,
      onSuccess: (professionals: Object) => professionals,
      onError: () => console.log('Request to advisers/people failed!') // eslint-disable-line
    });
  }


  state =
    typeof window === 'undefined'
      ? {
        fetching: false,
        professionals: this.props.staticContext ? this.props.staticContext.initialData.professionals.data : [],
        paging: this.props.staticContext ? this.props.staticContext.initialData.professionals.paging : {},
        page: this.props.params.page > 0 ? parseInt(this.props.params.page, 10) : 1,
        offset: this.props.params.page > 0 ? (this.props.params.page - 1) * PER_PAGE : 0
      }
      : {
        fetching: false,
        professionals: (window.initialData && window.initialData.professionals.data) || [],
        paging: (window.initialData && window.initialData.professionals.paging) || {},
        page: this.props.params.page > 0 ? parseInt(this.props.params.page, 10) : 1,
        offset: this.props.params.page > 0 ? (this.props.params.page - 1) * PER_PAGE : 0
      };

  mounted: boolean = false;

  scrollFrame: number | null = null;

  componentDidMount() {
    this.mounted = true;

    window && window.addEventListener('scroll', this.handleScroll, { passive: true });

    if (!this.state.professionals.length) {
      this.setState({ fetching: true });
      Professionals.loadInitialData('', {}, this.props.match, this.props.params)
        .then(({ data, paging }) => {
          if (!this.mounted) return;

          this.setState({
            professionals: data,
            paging,
            page: this.props.params.page > 0 ? parseInt(this.props.params.page, 10) : 1,
            offset: this.props.params.page > 0 ? (this.props.params.page - 1) * PER_PAGE : null
          });
        })
        .then(() => {
          if (this.mounted) this.setState({ fetching: false });
        });
    }
  }

  componentWillUnmount() {
    this.mounted = false;

    window && window.removeEventListener('scroll', this.handleScroll);

    if (this.scrollFrame !== null) {
      window.cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = null;
    }
  }

  redirectTo = (url: string) => {
    this.props.history.push(url);
  };

  loadNextPage = () => {
    this.setState({ fetching: true });
    const { page } = this.state;
    const nextPage = parseInt(page, 10) + 1;

    fetchAdvisers({
      offset: (nextPage - 1) * PER_PAGE,
      onSuccess: ({ data, paging }: Object) => {
        if (!this.mounted) return;

        this.setState({
          professionals: [...this.state.professionals, ...data],
          paging,
          page: nextPage,
          offset: this.props.params.page > 0 ? (this.props.params.page - 1) * PER_PAGE : null
        });
      },
      onError: () => { }
    }).then(() => {
      if (this.mounted) this.setState({ fetching: false });
    });
  };

  // El evento scroll se dispara varias veces por frame y la medición lee
  // offsetHeight/getBoundingClientRect, que fuerzan layout. Con requestAnimationFrame
  // se mide como mucho una vez por frame, y el listener es pasivo para no bloquear
  // el scroll del navegador.
  handleScroll = () => {
    if (this.scrollFrame !== null) return;

    this.scrollFrame = window.requestAnimationFrame(this.measureScroll);
  };

  measureScroll = () => {
    this.scrollFrame = null;

    if (typeof document === 'undefined') {
      return;
    }

    if (!document.documentElement) {
      return;
    }

    const OFFSET = 700;
    if (document.documentElement.offsetHeight - window.scrollY - window.innerHeight < OFFSET) {
      if (
        !this.state.fetching &&
        this.state.professionals.length &&
        this.state.paging.links.find(link => link.rel === 'next')
      ) {
        this.loadNextPage();
      }
    }
  };

  handleLoadMoreClick = () => {
    this.loadNextPage();
  };

  render() {
    const { professionals, paging, fetching, page } = this.state;
    const { toggleModal } = this.props;

    const prevPath = getPagePath(page - 1);
    const nextPath = getPagePath(page + 1);
    const currentPath = getPagePath(page);

    const paginationReady = paging.hasOwnProperty('totalItems');
    const pages = Math.ceil(paging.totalItems / paging.limit);

    return (
      <Fragment>
        <InsideNavigation
          links={[
            {
              title: 'Empresas',
              path: '/asesores/encuentra-un-asesor/empresas'
            }
          ]}
          currentPath={currentPath}
          buttonText="¿Necesitas ayuda?"
          redirectTo={this.redirectTo}
          toggleModal={toggleModal}
        />
        <MainGrey minHeight={!professionals.length}>
          <Head
            title="Encuentra un asesor financiero - Profesionales - Finect Asesores"
            description="Encuentra un asesor financiero de confianza que te ayude a planificar tus finanzas para conseguir tus objetivos."
            robots="index, follow"
            canonical={currentPath}
            withoutName
            openGraph={buildOpenGraph({
              title: 'Encuentra un asesor financiero - Profesionales - Finect Asesores',
              description:
                'Encuentra un asesor financiero de confianza que te ayude a planificar tus finanzas para conseguir tus objetivos.',
              url: currentPath
            })}
            twitter={buildTwitter({
              title: 'Encuentra un asesor financiero - Profesionales - Finect Asesores',
              description:
                'Encuentra un asesor financiero de confianza que te ayude a planificar tus finanzas para conseguir tus objetivos.'
            })}
          >
            {/* Sin Fragment: react-helmet descarta en silencio los hijos envueltos en uno. */}
            {paginationReady && page > 1 ? <link rel="prev" href={prevPath} /> : null}
            {paginationReady && pages > page ? <link rel="next" href={nextPath} /> : null}
          </Head>

          <Datalayer id="Scene" type="ready" params={{ pageType: 'advisersProfessionals' }} />
          <Datalayer id="Scene" type="pageType" params={{ pageType: 'advisersProfessionals' }} />

          <NewSectionInner>
            <ListHeader>
              <H1 large>Encuentra un asesor</H1>
            </ListHeader>
            <CardList>
              {professionals.map((professional, index) => (
                // $FlowFixMe
                <ProfessionalCard key={professional.id} index={index} {...professional} />
              ))}
            </CardList>
            <AdvisersPager
              paging={paging}
              page={page}
              fetching={fetching}
              nextPath={nextPath}
              onLoadMore={this.handleLoadMoreClick}
            />
          </NewSectionInner>
        </MainGrey>
        <BottomCTA toggleModal={toggleModal} white />
        <BottomNavigation
          links={[
            { title: 'Asesores', path: '/asesores' },
            {
              title: 'Encuentra un asesor',
              path: '/asesores/encuentra-un-asesor'
            },
            { title: 'Profesionales' }
          ]}
        />
      </Fragment>
    );
  }
}

export default withParams(Professionals);
